// ============================================================================
// LE PANNEAU ANCRÉ — un seul comportement d'ouverture/fermeture pour tout le dépôt.
//
// ⚠️ EXTRAIT DU PANNEAU DE COLONNES, qui était le seul à tout faire : focus rendu au déclencheur,
// Échap, fermeture au défilement d'un ancêtre AVEC l'exception de son propre défilement, bornage à la
// fenêtre, voile qui capture le clic extérieur. Les trois autres panneaux du dépôt en ont chacun une
// part — et l'un d'eux n'a AUCUNE sortie (ni Échap, ni clic extérieur, ni clavier). Réécrire un
// popover, c'est réécrire ces oublis.
//
// ⚠️ LE VOILE EST TRANSPARENT, ET IL EXISTE QUAND MÊME. Il ne peint rien : un popover n'est pas modal.
// Il est là pour trois raisons — capturer le clic extérieur EN UN SEUL ENDROIT (au lieu d'un écouteur
// global monté en permanence), empêcher ce clic de traverser (fermer un menu ne doit pas activer la
// ligne de tableau qui est dessous), et garantir qu'il n'existe qu'UNE sortie de clic, donc qu'aucun
// appelant ne peut oublier de rendre le focus.
// ============================================================================
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { anchorPanel, POPOVER, POPOVER_GEOM, type PanelGeom, type PanelPlacement } from '@orgazone/ui-tokens';
import { escapeIsMine, useOverlayLayer } from './overlay';

export interface PopoverTriggerApi {
  ref: RefObject<HTMLButtonElement | null>;
  'aria-expanded': boolean;
  'aria-haspopup': 'dialog' | 'listbox' | 'menu';
  'aria-controls': string;
  onClick: () => void;
}

export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Le déclencheur reçoit sa `ref` et ses attributs ARIA — l'appelant ne les écrit pas à la main. */
  trigger: (api: PopoverTriggerApi) => ReactNode;
  children: ReactNode | ((api: { close: () => void }) => ReactNode);
  /** Identifiant du panneau (lie `aria-controls`). */
  id: string;
  /** ⚠️ OBLIGATOIRE : un panneau sans nom accessible est annoncé « dialogue », et rien d'autre. */
  label: string;
  role?: 'dialog' | 'listbox' | 'menu';
  geom?: Partial<PanelGeom>;
  /**
   * Le focus va-t-il DANS le panneau à l'ouverture ?
   * `panel` (défaut) : le panneau prend le focus — sinon Échap n'a nulle part où être entendu.
   * `keep` : le focus RESTE au déclencheur (champ de recherche qu'on continue de taper), et c'est
   * alors à l'appelant de porter `aria-activedescendant`.
   */
  focus?: 'panel' | 'keep';
  /** Change → ferme. L'appelant y câble sa route, sans que `ui-web` dépende du routeur. */
  closeKey?: unknown;
  /**
   * Clavier du PANNEAU (flèches, Entrée…).
   * ⚠️ POSÉ ICI ET NON PAR L'APPELANT SUR SON CONTENU : un événement clavier remonte de l'élément
   * focalisé vers ses ancêtres, il ne descend jamais. Un gestionnaire posé sur un enfant du panneau
   * n'est donc jamais atteint quand c'est le panneau qui a le focus — le cas de toute liste courte.
   */
  onPanelKeyDown?: (e: React.KeyboardEvent, close: () => void) => void;
  /**
   * L'option actuellement visée, quand le panneau lui-même porte le focus.
   * ⚠️ `aria-activedescendant` doit vivre sur l'élément QUI A LE FOCUS : posé ailleurs, il ne désigne
   * rien et le lecteur d'écran n'annonce aucun déplacement.
   */
  activeDescendantId?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * LES SORTIES D'ENVIRONNEMENT d'un panneau ancré — le défilement et le redimensionnement.
 *
 * Un panneau positionné par mesure ne suit pas ce qui bouge sous lui : il désignerait une ligne qui
 * n'est plus là. On le referme donc, plutôt que de le laisser mentir sur ce qu'il décrit.
 *
 * ⚠️ MAIS À LA SOURIS SEULEMENT (2026-08-25). Sur un écran tactile, ces deux événements ne décrivent
 * plus un environnement qui bouge : ce sont le doigt qui fait défiler la LISTE, le clavier virtuel qui
 * s'ouvre ou se referme, la barre d'adresse du navigateur qui se replie au premier geste. Autrement
 * dit, ce que l'utilisateur PRODUIT en se servant du panneau — et qui le lui retirait sous les doigts
 * (« impossible de faire défiler la liste, ça la ferme », retour terrain).
 *
 * ⚠️ AU DOIGT, ON SUIT — ON NE SE CONTENTE PAS DE NE PAS FERMER (correctif du correctif, même jour).
 * Ne rien faire réglait le symptôme et laissait le VRAI problème : le corps du tiroir défile sous un
 * panneau en position fixe, qui restait donc collé à des coordonnées périmées pendant que son
 * déclencheur s'en allait (« c'est tout l'écran qui descend, pas le contenu de la liste »). C'est
 * précisément ce que la fermeture au défilement protégeait — et la protection ne valait que parce
 * qu'il n'y avait pas d'autre réponse. Il y en a une : REMESURER. Le panneau reste alors sur son
 * déclencheur, ce qui est la seule chose qu'on lui demande.
 *
 * ⚠️ POURQUOI PAR POINTEUR ET NON PAR EXEMPTIONS. Trois passes de correctifs partiels ont précédé
 * celle-ci — cible dans le panneau, focus dans le panneau, `overscroll-behavior` — chacune fermant un
 * chemin, aucune les fermant tous : un défilement du DOCUMENT n'est contenu dans aucun panneau, et un
 * clavier qui se referme rend le focus AVANT d'émettre son `resize`. La condition par pointeur les
 * couvre d'un coup, et elle dit la vraie raison plutôt que d'énumérer des symptômes.
 *
 * ⚠️ AUCUNE SORTIE N'EST PERDUE au doigt : la tape hors du panneau, le choix d'une option et Échap
 * ferment toujours. Seules les fausses sorties disparaissent.
 *
 * ⚠️ EXTRAIT PARCE QU'ILS SONT DEUX (règle de 2) : `Popover` et le sélecteur de colonnes du tableau
 * portaient la même mécanique, et le correctif tactile n'avait été appliqué qu'à l'un — l'autre gardait
 * exactement le défaut qu'on venait de corriger.
 */
export function useSortiesDEnvironnement(
  open: boolean, panelRef: RefObject<HTMLElement | null>, close: () => void, suivre?: () => void,
): void {
  useEffect(() => {
    if (!open) return;
    const auDoigt = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    // Son PROPRE défilement ne concerne personne d'autre : la liste est bornée, elle a le droit de défiler.
    const dehors = (e: Event) => !(e.target instanceof Node && panelRef.current?.contains(e.target));
    const surDefilement = (e: Event) => { if (dehors(e)) { if (auDoigt) suivre?.(); else close(); } };
    const onSize = () => { if (auDoigt) suivre?.(); else close(); };
    window.addEventListener('scroll', surDefilement, true);
    window.addEventListener('resize', onSize);
    return () => {
      window.removeEventListener('scroll', surDefilement, true);
      window.removeEventListener('resize', onSize);
    };
  }, [open, panelRef, close, suivre]);
}

/**
 * L'ANCÊTRE QUI SERT DE REPÈRE À `position: fixed` À LA PLACE DE LA FENÊTRE, ou `null` s'il n'y en a pas.
 *
 * ⚠️ `fixed` n'est relatif à la fenêtre que si aucun ancêtre n'établit de bloc conteneur : une transformation, un
 * filtre, un FLOU D'ARRIÈRE-PLAN, une perspective, un confinement ou une requête de conteneur en font un. La barre
 * VITRÉE de la Console (`backdrop-filter`) en est un : un panneau posé dedans se décalait de la largeur du menu, et son
 * voile ne couvrait plus que la barre — le clic sur la page ne le fermait plus.
 */
export function fixedContainingBlock(from: Element | null): Element | null {
  const actif = (v: string | null | undefined) => !!v && v !== 'none';
  for (let e = from; e && e !== document.documentElement; e = e.parentElement) {
    const s = getComputedStyle(e);
    const v = (p: string) => s.getPropertyValue(p);
    if (
      ['transform', 'translate', 'rotate', 'scale', 'perspective', 'filter', 'backdrop-filter', '-webkit-backdrop-filter'].some((p) => actif(v(p)))
      || /\b(layout|paint|strict|content)\b/.test(v('contain'))
      || /\b(transform|translate|rotate|scale|perspective|filter|backdrop-filter)\b/.test(v('will-change'))
      || (actif(v('container-type')) && v('container-type') !== 'normal')
    ) return e;
  }
  return null;
}

/**
 * Panneau ancré à un déclencheur, borné à la fenêtre, avec toutes ses sorties.
 * ⚠️ Le placement est MESURÉ à l'ouverture (`position: fixed`) : un panneau en flux serait rogné par
 * le premier ancêtre en `overflow`, et tous les panneaux de ce produit vivent dans des conteneurs
 * défilants bornés.
 */
export function Popover({
  open, onOpenChange, trigger, children, id, label,
  role = 'dialog', geom, focus = 'panel', closeKey,
  onPanelKeyDown, activeDescendantId, className, style,
}: PopoverProps) {
  // `ox`/`oy` : le coin du repère de `fixed` quand ce n'est pas la fenêtre (`fixedContainingBlock`). Le placement reste
  // calculé en coordonnées de FENÊTRE — c'est là que vivent le déclencheur et les bords à respecter —, on ne le
  // ramène au repère qu'au rendu.
  const [pos, setPos] = useState<(PanelPlacement & { ox: number; oy: number }) | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const focusPose = useRef(false);

  // ⚠️ LA GÉOMÉTRIE VIT DANS UNE RÉF, pas dans les dépendances : la plupart des appelants la
  // composent en ligne, donc elle change d'identité à chaque rendu. Dans les dépendances, la mesure
  // se relançait à chaque rendu — et comme elle pose un état, elle provoquait le rendu suivant.
  const geomRef = useRef(geom);
  geomRef.current = geom;

  const mesurer = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // ⚠️ DEPUIS LE PARENT DU DÉCLENCHEUR : le panneau est rendu à côté de lui, il partage donc ses ancêtres.
    const repere = fixedContainingBlock(el.parentElement)?.getBoundingClientRect();
    setPos({
      ...anchorPanel(
        { left: r.left, right: r.right, top: r.top, bottom: r.bottom },
        { width: window.innerWidth, height: window.innerHeight },
        { ...POPOVER_GEOM, ...geomRef.current },
      ),
      ox: repere?.left ?? 0,
      oy: repere?.top ?? 0,
    });
  }, []);

  useLayoutEffect(() => { if (open) mesurer(); }, [open, mesurer]);

  /**
   * LE chemin de fermeture — il n'y en a qu'un, et il REND LE FOCUS au déclencheur.
   * ⚠️ `preventScroll` : sur une fermeture PROVOQUÉE par un défilement, rendre le focus ferait remonter
   * la page au déclencheur — l'écran se battrait avec le geste de l'utilisateur.
   */
  const close = useCallback(() => {
    onOpenChange(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, [onOpenChange]);

  /**
   * SUIVRE SON DÉCLENCHEUR — la réponse tactile au défilement, à la place de la fermeture.
   * ⚠️ IL SE FERME QUAND MÊME SI LE DÉCLENCHEUR QUITTE L'ÉCRAN, et ce n'est pas un détail : un panneau
   * qui désigne un contrôle qu'on ne voit plus est un artefact flottant — la chose même que la
   * fermeture au défilement protégeait. On garde la protection, on change seulement sa condition.
   */
  const suivre = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) { close(); return; }
    mesurer();
  }, [close, mesurer]);

  // Le panneau prend le focus à l'ouverture (sauf mode `keep`) : sans lui, Échap n'a nulle part où être
  // entendu et l'utilisateur au clavier doit retraverser la liste pour revenir au déclencheur.
  useEffect(() => {
    if (!open) { focusPose.current = false; return; }
    if (focus !== 'panel' || focusPose.current || !panelRef.current) return;
    focusPose.current = true;
    panelRef.current.focus({ preventScroll: true });
  }, [open, pos, focus]);

  // Les quatre sorties d'environnement. ⚠️ Le défilement PROPRE du panneau est exempté : l'écouteur en
  // capture interceptait ses événements, donc le seul cas où un panneau a besoin de défiler (plus de
  // contenu que de place) était celui où il se refermait, à la molette comme aux flèches.
  useSortiesDEnvironnement(open, panelRef, close, suivre);
  // ⚠️ ÉCHAP FERME LE PANNEAU, PAS LA MODALE QUI L'HÉBERGE : c'est la PILE des surcouches qui le garantit — le panneau,
  //    ouvert le dernier, est seul à répondre, et marque l'Échap traité. Un `stopPropagation` ne le pouvait pas : la
  //    modale écoute le même document, et son écouteur, posé le premier, passait le premier.
  const isTop = useOverlayLayer(open);
  useEffect(() => {
    if (!open) return;
    const surTouche = (e: KeyboardEvent) => {
      if (!escapeIsMine(e, isTop)) return;
      e.preventDefault();
      close();
    };
    document.addEventListener('keydown', surTouche);
    return () => { document.removeEventListener('keydown', surTouche); };
  }, [open, close, isTop]);

  // Cinquième sortie : un changement de contexte de l'app (route, magasin actif…). Sans elle, un panneau
  // reste ouvert au-dessus d'un écran qu'il ne décrit plus.
  const premierRendu = useRef(true);
  useEffect(() => {
    if (premierRendu.current) { premierRendu.current = false; return; }
    if (open) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on ne réagit QU'au changement de `closeKey`
  }, [closeKey]);

  return (
    <>
      {trigger({
        ref: triggerRef,
        'aria-expanded': open,
        'aria-haspopup': role,
        'aria-controls': id,
        onClick: () => onOpenChange(!open),
      })}
      {open && pos && (
        <>
          <div
            onClick={close}
            // ⚠️ SOUS UN REPÈRE QUI N'EST PAS LA FENÊTRE, le voile se pose sur la FENÊTRE quand même : `inset: 0` ne
            //    couvrirait que le repère, et le clic sur le reste de la page traverserait sans fermer.
            style={{
              position: 'fixed', background: 'transparent', zIndex: POPOVER.zScrim,
              ...(pos.ox || pos.oy ? { left: -pos.ox, top: -pos.oy, width: '100vw', height: '100vh' } : { inset: 0 }),
            }}
          />
          <div
            ref={panelRef}
            id={id}
            role={role}
            aria-label={label}
            tabIndex={focus === 'panel' ? -1 : undefined}
            aria-activedescendant={activeDescendantId}
            onKeyDown={onPanelKeyDown ? (e) => onPanelKeyDown(e, close) : undefined}
            className={['oz-pop', className].filter(Boolean).join(' ')}
            data-placement={pos.placement}
            style={{
              position: 'fixed', left: pos.left - pos.ox, top: pos.top - pos.oy, width: pos.width, maxHeight: pos.maxHeight,
              // ⚠️ `overscroll-behavior: contain` (correctif tactile 2026-08-24) : au bout de la liste —
              // ou sur une liste qui tient entière — le geste du doigt CHAÎNAIT au défileur parent (le
              // corps du tiroir de filtres), et ce scroll extérieur fermait le panneau : impossible de
              // faire défiler un picker au doigt. Le débordement reste contenu dans le panneau.
              overscrollBehavior: 'contain',
              zIndex: POPOVER.zPanel, overflow: 'auto', outline: 'none',
              background: 'var(--paper)', border: '1px solid var(--ink-200)',
              borderRadius: `var(--r-${POPOVER.radius})`, boxShadow: `var(--sh-${POPOVER.shadow})`,
              ...style,
            }}
          >
            {typeof children === 'function' ? children({ close: close }) : children}
          </div>
        </>
      )}
    </>
  );
}
